import { and, collection, doc, getDoc, getDocs, or, query, Timestamp, where } from "firebase/firestore";
import { httpsCallable } from "firebase/functions";
import { db, functions } from "../../firebase/firebase";
import { nameofNewBookingSchemaCollection, nameofNewBookingSchemaStudentId, nameofNewBookingSchemaTutorId, NewBookingView } from "../Types/NewBookingSchema";
import { convertToNumber, convertToBinary, AvailabitiesBinary } from "../../Utils/TimeSlotUtils";
import { getTimeZoneOffset } from "../../Utils/ScheduleTimeZoneConverter";
import moment from "moment-timezone";
import { BookingView, nameofBookingBaseStudentId, nameofBookingBaseTutorId, nameofBookingSchemaCollection, nameofBookingSchemaDeleted, nameofSessionSchemaCollection, nameofSessionSchemaSessionTimeStamp, SessionView } from "../Types/BookingSchema";

export async function BookNewSession(TutorId: string, SubjectId: string, Duration: number, BookedTimes: AvailabitiesBinary, TutorTimeZone: string, StartDate?: Timestamp, IsRecurring?: boolean) {
    var timeOffset = - getTimeZoneOffset(moment.tz.guess(), TutorTimeZone) / 30;
    if (timeOffset < 0) {
        timeOffset = 48 + timeOffset;
    }
    var combinedString = getCombinedTimeSlotString(BookedTimes);

    if (combinedString.length !== 336) {
        throw new Error("Invalid time slots");
    }

    var tempTimeSlot = combinedString + combinedString;
    const offsetTimeSlot = tempTimeSlot.slice(timeOffset, timeOffset + 336);
    var timeSlots: number[] = [];
    for (var i = 0; i < 7; i++) {
        var startIndex = i * 48;
        var endIndex = startIndex + 48;
        var timeSlot = offsetTimeSlot.slice(startIndex, endIndex);
        timeSlots.push(convertToNumber(timeSlot));
    }

    const newBooking = httpsCallable(functions, "BookNewSession");

    await newBooking({
        TutorId: TutorId,
        SubjectId: SubjectId,
        Duration: Duration,
        TimeSlotMonday: timeSlots[0],
        TimeSlotTuesday: timeSlots[1],
        TimeSlotWednesday: timeSlots[2],
        TimeSlotThursday: timeSlots[3],
        TimeSlotFriday: timeSlots[4],
        TimeSlotSaturday: timeSlots[5],
        TimeSlotSunday: timeSlots[6],
        StartDate: StartDate ?? null,
        IsRecurring: IsRecurring ?? false,
    });
}

export async function UpdateBookingNotes(bookingId: string, notes: string) {
    const updateBookingNotes = httpsCallable(functions, "UpdateBookingNotes");

    await updateBookingNotes({BookingId: bookingId, Notes: notes});
}

export async function CancelNewBooking(bookingId: string) {
    const cancelBooking = httpsCallable(functions, "CancelNewBooking");

    await cancelBooking({BookingId: bookingId});
}

export async function ConfirmBooking(bookingId: string, timeSlotIndex96: number, day: string, notes: string) {
    const confirmBooking = httpsCallable(functions, "ConfirmBooking");
    
    await confirmBooking({BookingId: bookingId, TimeSlotIndex96: timeSlotIndex96, Day: day, Notes: notes});
}

export async function CancelBooking(bookingId: string) {
    const cancelBooking = httpsCallable(functions, "CancelBooking");

    await cancelBooking({BookingId: bookingId});
}

export async function GetAllNewBookings(userId: string): Promise<NewBookingView[]> {
    const bookingRef = collection(db, nameofNewBookingSchemaCollection);
    const bookingQuery = query(bookingRef, or(where(nameofNewBookingSchemaTutorId, "==", userId), where(nameofNewBookingSchemaStudentId, "==", userId)));
    const bookingList = [] as NewBookingView[];
    const bookingDocs = await getDocs(bookingQuery);
    bookingDocs.docs.forEach((doc) => {
        bookingList.push({...doc.data(), Id: doc.id} as NewBookingView);
    });
    return bookingList;
}

export async function GetSessionsForUser(userId: string): Promise<SessionView[]> {
    const sessionRef = collection(db, nameofSessionSchemaCollection);
    const startOfWeek = moment().startOf("week").toDate();
    const sessionQuery = query(sessionRef, and(or(where(nameofBookingBaseStudentId, "==", userId), where(nameofBookingBaseTutorId, "==", userId)), where(nameofSessionSchemaSessionTimeStamp, ">=", Timestamp.fromDate(startOfWeek))));
    const sessionList = [] as SessionView[];
    const sessionDocs = await getDocs(sessionQuery);
    sessionDocs.docs.forEach((doc) => {
        sessionList.push({...doc.data(), Id: doc.id} as SessionView);
    });
    return sessionList;
}

export async function GetSession(sessionId: string): Promise<SessionView> {
    const sessionDoc = doc(db, nameofSessionSchemaCollection, sessionId);
    const session = await getDoc(sessionDoc);
    return {...session.data(), Id: session.id} as SessionView;
}

export async function UpdateSessionNotes(sessionId: string, notes: string) {
    const updateSessionNotes = httpsCallable(functions, "UpdateSessionNotes");

    await updateSessionNotes({SessionId: sessionId, Notes: notes});
}

export async function CancelSession(sessionId: string) {
    const cancelSession = httpsCallable(functions, "CancelSession");

    await cancelSession({SessionId: sessionId});
}

export async function ReOpenSession(sessionId: string) {
    const reopenSession = httpsCallable(functions, "ReOpenSession");

    await reopenSession({SessionId: sessionId});
}

export async function GetBookingsForUser(userId: string): Promise<BookingView[]> {
    const bookingRef = collection(db, nameofBookingSchemaCollection);
    const bookingQuery = query(bookingRef, and(or(where(nameofBookingBaseStudentId, "==", userId), where(nameofBookingBaseTutorId, "==", userId)), where(nameofBookingSchemaDeleted, "==", false)));
    const bookingList = [] as BookingView[];
    const bookingDocs = await getDocs(bookingQuery);
    bookingDocs.docs.forEach((doc) => {
        bookingList.push({...doc.data(), Id: doc.id} as BookingView);
    });
    return bookingList;
}

export async function GetAllNewBookingsForTutorStudent(tutorId: string, userId:string): Promise<NewBookingView[]> {
    const bookingRef = collection(db, nameofNewBookingSchemaCollection);
    const bookingQuery = query(bookingRef, where(nameofNewBookingSchemaTutorId, "==", tutorId), where(nameofNewBookingSchemaTutorId, "==", userId));
    const bookingList = [] as NewBookingView[];
    const bookingDocs = await getDocs(bookingQuery);
    bookingDocs.docs.forEach((doc) => {
        bookingList.push({...doc.data(), Id: doc.id} as NewBookingView);
    });
    return bookingList;
}

function getCombinedTimeSlotString(BookedTimes: AvailabitiesBinary): string {
    const emptyString = convertToBinary(0);
    return (BookedTimes.Monday?? emptyString) + (BookedTimes.Tuesday?? emptyString) + (BookedTimes.Wednesday?? emptyString) + (BookedTimes.Thursday ?? emptyString) + (BookedTimes.Friday ?? emptyString) + (BookedTimes.Saturday ?? emptyString) + (BookedTimes.Sunday ?? emptyString);
}